import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import * as Icons from "lucide-react";
import { useFormContext } from "react-hook-form";
import FilterProvider, {
  FilterState,
} from "./(overview)/(context)/filter";
import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import React from "react";
import { formatDate } from "@/lib/date";
import FillTask from "@/components/features/tasks/fill";
import { filterObjectsByValue } from "@/lib/filter";
import PriorityFormat from "@/components/features/tasks/priority-format";

type Props = {
  onRefetchReady?: (fn: () => void) => void;
};

const MyTasks = ({ onRefetchReady }: Props) => {
  const { trpc } = useTRPC();
  const { watch, setValue } = useFormContext<FilterState>();
  const filterState = watch();

  const [search, setSearch] = React.useState<string>('');

  const query = useQuery({
    queryKey: ["assigned-tasks", filterState],
    queryFn: async () => {
      const tasks = await trpc.tasks.getAssigned.query();
      return tasks;
    },
    enabled: false,
  });

  React.useEffect(() => {
    onRefetchReady?.(() => query.refetch());
  }, [query.refetch]);

  React.useEffect(() => {
    query.refetch();
  }, [filterState.page]);

  return (
    <div className="h-full overflow-auto">
      <div className="flex flex-col justify-between h-full gap-4">
        <div className="flex items-center justify-between gap-4">
          <Input
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            placeholder="Zoek naar taak"
            startContent={<Icons.Search className="w-4 h-4" />}
            className="w-fit"
          />                      
        </div>
        <div className="bg-foreground/10 rounded-md border pb-2 pr-2 pl-2 overflow-auto h-full">
          <Table className='overflowclip w-full relative' divClassname='overflow-y-scroll max-h-[100%]'>
            <TableHeader className="sticky top-0 w-full border-b bg-sidebar/50 backdrop-blur-md z-10">
              <TableRow>
                <TableHead>Type rapport</TableHead>
                <TableHead>Type taak</TableHead>
                <TableHead>Omschrijving</TableHead>
                <TableHead>Prioriteit</TableHead>
                <TableHead>Deadline</TableHead>
                <TableHead>Aangeboden door</TableHead>
                <TableHead>Notitie</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!query.isLoading && query?.data?.tasks?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-4">
                    Er zijn geen taken toegewezen aan jou.
                  </TableCell>
                </TableRow>
              ) : (
                filterObjectsByValue(query?.data?.tasks || [], search).map((task, i) => (
                  <FillTask 
                    onSuccess={() => {
                      query.refetch();
                    }}
                    task_id={task.id}
                    trigger={
                      <TableRow key={i} className="hover:bg-input/50 cursor-pointer group/row">
                        <TableCell>
                          <p className="font-medium group-hover/row:underline">{task?.report ? task?.report.type?.join(' - ') : 'geen rapport aanwezig'}</p>
                        </TableCell>
                        <TableCell>
                          {task?.mongo_player_id ? 'Speler' : task?.ext_club_id ? 'Club' : task?.ext_competition_id ? 'Competitie' : ' - '}
                        </TableCell>
                        <TableCell>{task?.mongo_player?.name ?? task?.ext_club?.name ?? task?.ext_competition?.name ?? ' - '}</TableCell>
                        <TableCell>
                          <PriorityFormat 
                            priority={task?.priority}
                          />
                        </TableCell>
                        <TableCell>{task?.expiration_date ? formatDate(task?.expiration_date) : ''}</TableCell>
                        <TableCell>{task?.offered_by?.contact_name}</TableCell>
                        <TableCell>{task?.note}</TableCell>
                      </TableRow>
                    }
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm font-medium text-foreground/50">
            {query?.data?.total || 0} rijen gevonden
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              onClick={() => setValue("page", 1)}
              disabled={parseInt(filterState?.page?.toString() || "0") <= 1}
            >
              <Icons.ChevronsLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                setValue("page", Math.max(1, Number(filterState.page) - 1))
              }
              disabled={parseInt(filterState?.page?.toString() || "0") <= 1}
            >
              <Icons.ChevronLeft className="w-4 h-4" />
            </Button>
            <Input
              value={filterState.page}
              onChange={(e) => {
                setValue("page", e.currentTarget.valueAsNumber);
              }}
              onBlur={() => {
                query.refetch();
              }}
              type="number"
              min={1}
              max={query?.data?.pages || 1}
            />
            <Button
              variant="outline"
              onClick={() =>
                setValue(
                  "page",
                  Math.min(
                    Number(query?.data?.pages || 1),
                    Number(filterState.page) + 1
                  )
                )
              }
              disabled={Number(filterState.page) >= Number(query?.data?.pages)}
            >
              <Icons.ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() => setValue("page", query?.data?.pages || 1)}
              disabled={Number(filterState.page) >= Number(query?.data?.pages)}
            >
              <Icons.ChevronsRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ({ ...props }: Props) => {
  return (
    <FilterProvider>
      <MyTasks {...props} />
    </FilterProvider>
  );
};
